import type { DynamicIslandActivity } from '../../../context/dynamicIslandTypes'
import { ThemeDynamicIslandMorph, useDynamicIslandExpand } from './ThemeDynamicIsland'
import { DynamicIslandExpandedSummary } from './DynamicIslandExpandedSummary'
import { DynamicIslandCategoryPreviewRow } from './previews/DynamicIslandCategoryPreviewRow'
import { DynamicIslandSoloPreview } from './previews/DynamicIslandSoloPreview'

type Props = {
  activities: DynamicIslandActivity[]
  selectedId: string | null
  onSelectId: (id: string) => void
  expanded: boolean
  setExpanded: (next: boolean) => void
  angular: boolean
}

function MultiSourceExpandedSummaries({
  activities,
  selectedId,
  angular,
}: {
  activities: DynamicIslandActivity[]
  selectedId: string
  angular: boolean
}) {
  return (
    <div className="glyphicai-dynamic-island-expanded-summary-stack">
      {activities.map((activity) => (
        <div
          key={activity.id}
          className={[
            'glyphicai-dynamic-island__pane',
            activity.id === selectedId ? 'glyphicai-dynamic-island__pane--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden={activity.id !== selectedId}
        >
          <DynamicIslandExpandedSummary activity={activity} angular={angular} compact />
        </div>
      ))}
    </div>
  )
}

function MultiSourceActivityView({
  activities,
  selected,
  onSelectId,
  expanded,
  onExpand,
  angular,
}: {
  activities: DynamicIslandActivity[]
  selected: DynamicIslandActivity
  onSelectId: (id: string) => void
  expanded: boolean
  onExpand: () => void
  angular: boolean
}) {
  const compactPreviewRow = (
    <DynamicIslandCategoryPreviewRow
      activities={activities}
      selectedId={selected.id}
      onSelectId={onSelectId}
      angular={angular}
      expanded={false}
      onExpand={onExpand}
    />
  )

  const expandedPreviewRow = (
    <DynamicIslandCategoryPreviewRow
      activities={activities}
      selectedId={selected.id}
      onSelectId={onSelectId}
      angular={angular}
      expanded
      onExpand={onExpand}
    />
  )

  return (
    <ThemeDynamicIslandMorph
      expanded={expanded}
      compact={compactPreviewRow}
      expandedPane={
        <div className="glyphicai-dynamic-island-multi-expanded flex w-full min-w-0 flex-col gap-1.5">
          {expandedPreviewRow}
          <MultiSourceExpandedSummaries
            activities={activities}
            selectedId={selected.id}
            angular={angular}
          />
        </div>
      }
    />
  )
}

function SoloSourceActivityView({
  activity,
  expanded,
  onExpand,
  angular,
}: {
  activity: DynamicIslandActivity
  expanded: boolean
  onExpand: () => void
  angular: boolean
}) {
  return (
    <ThemeDynamicIslandMorph
      expanded={expanded}
      compact={<DynamicIslandSoloPreview activity={activity} angular={angular} onExpand={onExpand} />}
      expandedPane={
        <DynamicIslandExpandedSummary activity={activity} angular={angular} compact layoutMode="solo" />
      }
    />
  )
}

export function ThemeDynamicIslandActivityView({
  activities,
  selectedId,
  onSelectId,
  expanded,
  setExpanded,
  angular,
}: Props) {
  const onExpand = useDynamicIslandExpand(setExpanded)
  const selected =
    activities.find((item) => item.id === selectedId) ?? activities[0] ?? null

  if (!selected) return null

  return (
    <div
      className={[
        'glyphicai-dynamic-island-activity',
        activities.length === 1 ? 'glyphicai-dynamic-island-activity--solo' : 'glyphicai-dynamic-island-activity--multi',
      ].join(' ')}
    >
      {activities.length === 1 ? (
        <SoloSourceActivityView
          activity={selected}
          expanded={expanded}
          onExpand={onExpand}
          angular={angular}
        />
      ) : (
        <MultiSourceActivityView
          activities={activities}
          selected={selected}
          onSelectId={onSelectId}
          expanded={expanded}
          onExpand={onExpand}
          angular={angular}
        />
      )}
    </div>
  )
}
